import { Injectable } from "@angular/core";
import { TokenPayload } from "../../features/auth/auth.model";
import { registerTypeModel } from "../../features/ticket-booking/ticket-booking.model";
import { VideoDetailsModel } from "../../features/visitor-voting/voting.model";

/**
 * Service for persisting and retrieving authentication-related data
 * in the browser's localStorage.
 */

@Injectable({ providedIn: "root" })
export class StorageService {
  private ACCESS_KEY = "access_token";
  private ID_KEY = "id_token";
  private REFRESH_KEY = "refresh_token";
  private EXPIRES_AT_KEY = "expires_at";
  private PAYLOAD_KEY = "id_token_payload";
  private USER_ID = "user_id";
  private PKCE_CODE_VERIFIER = "pkce_code_verifier";
  private EVENT_CODE = "event_code";
  private REGISTER_TYPE_SELECTED = "register_type_selected";
  private SUMMARY_ID = "summary_id";
  private LINK_LOGIN_REDIRECT = "link_login_redirect";
  private LINK_LOGIN_REDIRECT_QUERY_PARAM = "link_login_redirect_query_params";
  private RZP_CHECKOUT_ANON_ID = "rzp_checkout_anon_id";
  private RZP_DEVICE_ID = "rzp_device_id";
  private RZP_STORED_CHECKOUT_ID = "rzp_stored_checkout_id";
  private PAYMENT_DISCOUNT = "payment_discount";
  private PAYMENT_SUCCESS = "payment_success";
  private DISCOUNTS = "discounts";
  private USE_SUGGESTION = "use_suggestion";
  private USER_IMG_URL = "user_img_url";
  private USER_NAME = "user_name";
  private DISCOUNT_AMOUNT_POPUP = "discount_amount";
  private SHOW_PAYMENT_SUCCESS_POPUP = "show_payment_success";
  private STATE = "state";
  private SELECTED_PAVILION_NAME = "selected_pavilion_name";
  private SELECTED_PAVILION_IMG_URL = "selected_pavilion_img_url";
  private SELECTED_PAVILION_CODE = "selected_pavilion_code";
  private JUST_AUTHENTICATED = "justAuthenticated";
  private VIRTUAL_EVENT_PENDING = "virtualEventPending";
  private HAS_CATEGORY_FILLED = "hasCategoryFilled";
  private ACCESS_TO_VIRTUAL_EVENT = "accessToVirtualEvent";
  private IS_INVITE_ACCEPTED = "isInviteAccepted";
  private REF_SOURCE = "ref_source";
  private SELECTED_CATEGORY_NAME = "selected_category_name";
  private SELECTED_CATEGORY_IMG_URL = "selected_category_img_url";
  private SELECTED_VIDEO_DETAILS = "video_details";

  constructor() {}
  /**
   * Saves the OAuth2 access token.
   * @param access_token The bearer token for API authorization.
   */
  saveAccessToken(access_token: string): void {
    localStorage.setItem(this.ACCESS_KEY, access_token);
  }

  /**
   * Retrieves the stored access token.
   * @returns The access token, or `null` if none is stored.
   */
  getAccessToken(): string | null {
    return localStorage.getItem(this.ACCESS_KEY);
  }

  /**
   * Saves the OpenID Connect ID token.
   * @param id_token The JWT containing user identity claims.
   */
  saveIdToken(id_token: string): void {
    localStorage.setItem(this.ID_KEY, id_token);
  }

  /**
   * Retrieves the stored ID token.
   * @returns The ID token, or `null` if none is stored.
   */
  getIdToken(): string | null {
    return localStorage.getItem(this.ID_KEY);
  }

  /**
   * Saves the OAuth2 refresh token.
   * @param refresh_token Token used to obtain new access tokens.
   */
  saveRefreshToken(refresh_token: string): void {
    localStorage.setItem(this.REFRESH_KEY, refresh_token);
  }

  /**
   * Retrieves the stored refresh token.
   * @returns The refresh token, or `null` if none is stored.
   */
  getRefreshToken(): string | null {
    return localStorage.getItem(this.REFRESH_KEY);
  }

  /**
   * Saves the currently authenticated user's ID.
   * @param user_id Unique identifier of the logged-in user.
   */
  saveLoggedUserId(user_id: string): void {
    localStorage.setItem(this.USER_ID, user_id);
  }

  /**
   * Retrieves the stored user ID.
   * @returns The user ID, or `null` if none is stored.
   */
  getLoggedUserId(): string | null {
    return localStorage.getItem(this.USER_ID);
  }

  /**
   * Persists the token expiration timestamp.
   * @param expiry UNIX timestamp (in seconds) when the access token expires.
   */
  saveExpiresAt(expiry: string): void {
    localStorage.setItem(this.EXPIRES_AT_KEY, expiry);
  }

  /**
   * Retrieves the stored expiration timestamp.
   * @returns The timestamp as a number, or `null` if not set.
   */
  getExpiresAt(): number | null {
    const exp = localStorage.getItem(this.EXPIRES_AT_KEY);
    return exp ? parseInt(exp, 10) : null;
  }
  /**
   * Saves the payment discount information.
   * @param discount The discount amount or code applied during payment.
   */
  savePaymentDiscount(discount: string): void {
    localStorage.setItem(this.PAYMENT_DISCOUNT, discount);
  }
  /**
   * Retrieves the stored discounts information.
   * @returns The discounts as a string, or `null` if none is stored.
   */
  getDiscounts(): string | null {
    return localStorage.getItem(this.DISCOUNTS);
  }

  /**
   * Retrieves the stored access token.
   * @returns The link login redirect, or `null` if none is stored.
   */
  getLinkLoginRedirect(): string | null {
    return localStorage.getItem(this.LINK_LOGIN_REDIRECT);
  }

  /**
   * Saves the OpenID Connect ID token.
   * @param id_token The JWT containing user identity claims.
   */
  saveLinkLoginRedirect(redirect: string): void {
    localStorage.setItem(this.LINK_LOGIN_REDIRECT, redirect);
  }

  /**
   * Retrieves the login redirect query parameters from local storage.
   *
   * @returns {JSON | null} The parsed query parameters as a string if they exist, otherwise null.
   */
  getLinkLoginRedirectQueryParams(): Record<string, any> | null {
    const param = localStorage.getItem(this.LINK_LOGIN_REDIRECT_QUERY_PARAM);
    if (param) {
      return JSON.parse(param);
    } else {
      return null;
    }
  }

  /**
   * Saves the login redirect query parameters to local storage.
   *
   * @param query_param - The query parameters to be saved. This should be an object that can be serialized to JSON.
   */
  saveLinkLoginRedirectQueryParams(query_param: any): void {
    localStorage.setItem(
      this.LINK_LOGIN_REDIRECT_QUERY_PARAM,
      JSON.stringify(query_param)
    );
  }

  /**
   * Clears the link login redirect data from local storage.
   * This includes removing the LINK_LOGIN_REDIRECT and LINK_LOGIN_REDIRECT_QUERY_PARAM items.
   *
   * @returns {void} This method does not return a value.
   */
  clearLinkLoginRedirectData(): void {
    localStorage.removeItem(this.LINK_LOGIN_REDIRECT);
    localStorage.removeItem(this.LINK_LOGIN_REDIRECT_QUERY_PARAM);
  }

  /**
   * Saves the decoded JWT payload for quick access.
   * @param token_payload JSON-stringified TokenPayload object.
   */
  saveDecodedTokenPayload(token_payload: string): void {
    localStorage.setItem(this.PAYLOAD_KEY, token_payload);
  }

  /**
   * Retrieves and parses the stored JWT payload.
   * @returns The parsed TokenPayload, or `null` if none is stored.
   */
  getDecodedTokenPayload(): TokenPayload | null {
    const raw = localStorage.getItem(this.PAYLOAD_KEY);
    return raw ? (JSON.parse(raw) as TokenPayload) : null;
  }

  /**
   * Stores the PKCE code verifier used during OAuth login.
   * @param verifier The random string verifier.
   */
  saveCodeVerifier(verifier: string): void {
    localStorage.setItem(this.PKCE_CODE_VERIFIER, verifier);
  }

  /**
   * Retrieves the stored PKCE code verifier.
   * @returns The code verifier, or `null` if none is stored.
   */
  getCodeVerifier(): string | null {
    return localStorage.getItem(this.PKCE_CODE_VERIFIER);
  }

  /**
   * Saves the event code associated with the user's session.
   * @param event_code The unique code for the event.
   */
  saveEventCode(event_code: string): void {
    localStorage.setItem(this.EVENT_CODE, event_code);
  }
  /**
   * Retrieves the stored event code.
   * @returns The event code, or `null` if none is stored.
   */
  getEventCode(): string | null {
    return localStorage.getItem(this.EVENT_CODE);
  }

  /**
   * Saves the selected registration type for the event.
   * @param register_type The type of registration selected.
   */
  saveRegisterTypeSelected(register_type: string): void {
    localStorage.setItem(this.REGISTER_TYPE_SELECTED, register_type);
  }

  /**
   * Retrieves the selected registration type.
   * @returns The registration type, or `null` if none is stored.
   */
  getRegisterTypeSelected(): registerTypeModel[] | null {
    const raw = localStorage.getItem(this.REGISTER_TYPE_SELECTED);
    return raw ? (JSON.parse(raw) as registerTypeModel[]) : null;
  }

  /**
   * Saves the summary ID for the ticket booking.
   * @param summary_id The unique identifier for the ticket booking summary.
   */
  saveSummaryId(summary_id: string): void {
    localStorage.setItem(this.SUMMARY_ID, summary_id);
  }
  /**
   * Retrieves the stored summary ID.
   * @returns The summary ID, or `null` if none is stored.
   */
  getSummaryId(): string | null {
    return localStorage.getItem(this.SUMMARY_ID);
  }

  /**
   * Saves the discounts information.
   * @param discounts JSON-stringified string containing discount details.
   */
  saveDiscounts(discounts: any): void {
    localStorage.setItem(this.DISCOUNTS, discounts);
  }

  /**
   * Retrieves the stored payment discount.
   * @returns The payment discount, or `null` if none is stored.
   */
  getPaymentDiscount() {
    return localStorage.getItem(this.PAYMENT_DISCOUNT);
  }
  /**
   * Saves the Razorpay checkout anonymous ID.
   * @param id The anonymous ID used in Razorpay checkout.
   */
  savePaymentSuccessFlag(flag: string): void {
    localStorage.setItem(this.PAYMENT_SUCCESS, flag);
  }

  /**
   * Retrieves the stored Razorpay checkout anonymous ID.
   * @returns The anonymous ID, or `null` if none is stored.
   */
  getPaymentSuccessFlag(): string | null {
    return localStorage.getItem(this.PAYMENT_SUCCESS);
  }

  saveUseSuggestion(flag: string): void {
    localStorage.setItem(this.USE_SUGGESTION, flag);
  }

  getUseSuggestion(): string | null {
    return localStorage.getItem(this.USE_SUGGESTION);
  }

  /**
   * Saves the User Image URL.
   * @param url The URL of the user's image.
   */
  saveUserImgUrl(url: string | undefined | null): void {
    localStorage.setItem(this.USER_IMG_URL, url || "");
  }
  /**
   * Retrieves the stored User Image URL.
   * @returns The User Image URL, or `null` if none is stored.
   */
  getUserImgUrl(): string | null {
    return localStorage.getItem(this.USER_IMG_URL);
  }

  /**
   * Saves the User Name.
   * @param name The name of the user.
   */
  saveUserName(name: string): void {
    localStorage.setItem(this.USER_NAME, name);
  }

  /**
   * Retrieves the stored User Name.
   * @returns The User Name, or `null` if none is stored.
   */
  getUserName(): string | null {
    return localStorage.getItem(this.USER_NAME);
  }

  saveDiscountAmount(amount: string): void {
    localStorage.setItem(this.DISCOUNT_AMOUNT_POPUP, amount);
  }

  getDiscountAmount(): string | null {
    return localStorage.getItem(this.DISCOUNT_AMOUNT_POPUP);
  }

  saveShowPaymentSuccess(flag: string): void {
    localStorage.setItem(this.SHOW_PAYMENT_SUCCESS_POPUP, flag);
  }

  getShowPaymentSuccess(): string | null {
    return localStorage.getItem(this.SHOW_PAYMENT_SUCCESS_POPUP);
  }

  /**
   * Retrieves the value of the "state" item from local storage.
   *
   * @returns {string | null} The stored state value, or `null` if not found.
   */
  getState(): string | null {
    return localStorage.getItem(this.STATE);
  }

  /**
   * Saves the provided state string to the browser's local storage under the key "state".
   *
   * @param state - The state value to be saved in local storage.
   */
  saveState(state: string): void {
    localStorage.setItem(this.STATE, state);
  }

  /**
   * Retrieves the value associated with the `JUST_AUTHENTICATED` key from local storage.
   *
   * @returns The value of `JUST_AUTHENTICATED` from local storage, or `null` if the key does not exist.
   */
  getJustAuthenticated(): string | null {
    return localStorage.getItem(this.JUST_AUTHENTICATED);
  }

  /**
   * Saves the provided authentication status value to localStorage under the key specified by `JUST_AUTHENTICATED`.
   *
   * @param value - The value indicating the authentication status to be stored.
   */
  saveJustAuthenticated(value: string): void {
    localStorage.setItem(this.JUST_AUTHENTICATED, value);
  }

  /**
   * Retrieves the value associated with the virtual event pending key from localStorage.
   *
   * @returns The stored string value if present, or `null` if the key does not exist.
   */
  getVirtualEventPending(): string | null {
    return localStorage.getItem(this.VIRTUAL_EVENT_PENDING);
  }

  /**
   * Saves the provided value to local storage under the key specified by `VIRTUAL_EVENT_PENDING`.
   *
   * @param value - The string value to be stored as the pending virtual event.
   */
  saveVirtualEventPending(value: string): void {
    localStorage.setItem(this.VIRTUAL_EVENT_PENDING, value);
  }

  /**
   * Saves the category filled status to local storage.
   *
   * @param value - The value indicating whether the category has been filled.
   */
  saveHasCategoryFilled(value: string): void {
    localStorage.setItem(this.HAS_CATEGORY_FILLED, value);
  }

  /**
   * Saves the access status to a virtual event in local storage.
   *
   * @param value - A string representing whether the user has access to the virtual event.
   */
  saveHasAccessToVirtualEvent(value: string): void {
    localStorage.setItem(this.ACCESS_TO_VIRTUAL_EVENT, value);
  }

  /**
   * Retrieves the value indicating whether the category has been filled from local storage.
   *
   * @returns {string | null} The value associated with the category filled status, or `null` if not set.
   */
  getHasCategoryFilled(): string | null {
    return localStorage.getItem(this.HAS_CATEGORY_FILLED);
  }

  /**
   * Retrieves the access token or flag for the virtual event from local storage.
   *
   * @returns {string | null} The value associated with the virtual event access key, or `null` if not found.
   */
  getAccessToVirtualEvent(): string | null {
    return localStorage.getItem(this.ACCESS_TO_VIRTUAL_EVENT);
  }

  getIsInviteAccepted(): string | null {
    return localStorage.getItem(this.IS_INVITE_ACCEPTED);
  }

  saveIsInviteAccepted(value: string): void {
    localStorage.setItem(this.IS_INVITE_ACCEPTED, value);
  }

  /**
   * Saves the selected pavilion name to local storage.
   * @param pavilionName The name of the selected pavilion.
   */
  saveSelectedPavilionName(pavilionName: string): void {
    localStorage.setItem(this.SELECTED_PAVILION_NAME, pavilionName);
  }

  /**
   * Retrieves the selected pavilion name from local storage.
   * @returns The name of the selected pavilion, or `null` if not found.
   */
  getSelectedPavilionName(): string | null {
    return localStorage.getItem(this.SELECTED_PAVILION_NAME);
  }

  /**
   * Saves the selected pavilion image URL to local storage.
   * @param pavilionImgUrl The URL of the selected pavilion image.
   */
  saveSelectedPavilionImgUrl(pavilionImgUrl: string): void {
    localStorage.setItem(this.SELECTED_PAVILION_IMG_URL, pavilionImgUrl);
  }

  /**
   * Retrieves the selected pavilion image URL from local storage.
   * @returns The URL of the selected pavilion image, or `null` if not found.
   */
  getSelectedPavilionImgUrl(): string | null {
    return localStorage.getItem(this.SELECTED_PAVILION_IMG_URL);
  }

  /**
   * Saves the selected pavilion code to local storage.
   * @param pavilion_code The code of the selected pavilion.
   */
  saveSelectedPavilionCode(pavilion_code: string): void {
    localStorage.setItem(this.SELECTED_PAVILION_CODE, pavilion_code);
  }

  /**
   * Retrieves the selected pavilion code from local storage.
   * @returns The code of the selected pavilion, or `null` if not found.
   */
  getSelectedPavilionCode(): string | null {
    return localStorage.getItem(this.SELECTED_PAVILION_CODE);
  }

  /**
   * Saves the referral source to local storage.
   */
  saveRefSource(ref_source: string): void {
    localStorage.setItem(this.REF_SOURCE, ref_source);
  }

  /**
   * 
   * @returns The referral source from local storage, or `null` if not found.
   */
  getRefSource() {
    return localStorage.getItem(this.REF_SOURCE);
  }

  /**
   * Saves the selected category name to local storage.
   * @param categoryName The name of the selected category.
   */
  saveSelectedCategoryName(categoryName: string): void {
    localStorage.setItem(this.SELECTED_CATEGORY_NAME, categoryName);
  }

  /**
   * Retrieves the selected category name from local storage.
   * @returns The name of the selected category, or `null` if not found.
   */
  getSelectedCategoryName(): string | null {
    return localStorage.getItem(this.SELECTED_CATEGORY_NAME);
  }

  /**
   * Saves the selected category image URL to local storage.
   * @param categoryImgUrl The URL of the selected category image.
   */
  saveSelectedCategoryImgUrl(categoryImgUrl: string): void {
    localStorage.setItem(this.SELECTED_CATEGORY_IMG_URL, categoryImgUrl);
  }

  /**
   * Retrieves the selected category image URL from local storage.
   * @returns The URL of the selected category image, or `null` if not found.
   */
  getSelectedCategoryImgUrl(): string | null {
    return localStorage.getItem(this.SELECTED_CATEGORY_IMG_URL);
  }

  /**
   * Save video details to local storage.
   */
  saveSelectedVideoDetails(video_details: VideoDetailsModel): void {
    localStorage.setItem(
      this.SELECTED_VIDEO_DETAILS,
      JSON.stringify(video_details)
    );
  }

  /**
   * Get video details from local storage.
   */
  getSelectedVideoDetails(): VideoDetailsModel | null {
    const details = localStorage.getItem(this.SELECTED_VIDEO_DETAILS);
    return details ? JSON.parse(details) : null;
  }

  /**
   * Clears all authentication-related entries from localStorage.
   */
  clearAll(): void {
    localStorage.removeItem(this.ACCESS_KEY);
    localStorage.removeItem(this.ID_KEY);
    localStorage.removeItem(this.REFRESH_KEY);
    localStorage.removeItem(this.EXPIRES_AT_KEY);
    localStorage.removeItem(this.PAYLOAD_KEY);
    localStorage.removeItem(this.USER_ID);
    localStorage.removeItem(this.PKCE_CODE_VERIFIER);
    localStorage.removeItem(this.EVENT_CODE);
    localStorage.removeItem(this.REGISTER_TYPE_SELECTED);
    localStorage.removeItem(this.SUMMARY_ID);
    localStorage.removeItem(this.RZP_CHECKOUT_ANON_ID);
    localStorage.removeItem(this.RZP_DEVICE_ID);
    localStorage.removeItem(this.RZP_STORED_CHECKOUT_ID);
    localStorage.removeItem(this.PAYMENT_DISCOUNT);
    localStorage.removeItem(this.DISCOUNTS);
    localStorage.removeItem(this.PAYMENT_SUCCESS);
    localStorage.removeItem(this.USE_SUGGESTION);
    localStorage.removeItem(this.USER_IMG_URL);
    localStorage.removeItem(this.USER_NAME);
    localStorage.removeItem(this.DISCOUNT_AMOUNT_POPUP);
    localStorage.removeItem(this.SHOW_PAYMENT_SUCCESS_POPUP);
    localStorage.removeItem(this.STATE);
    localStorage.removeItem(this.JUST_AUTHENTICATED);
    localStorage.removeItem(this.VIRTUAL_EVENT_PENDING);
    localStorage.removeItem(this.HAS_CATEGORY_FILLED);
    localStorage.removeItem(this.ACCESS_TO_VIRTUAL_EVENT);
    localStorage.removeItem(this.IS_INVITE_ACCEPTED);
    localStorage.removeItem(this.SELECTED_CATEGORY_NAME);
    localStorage.removeItem(this.SELECTED_CATEGORY_IMG_URL);
    localStorage.removeItem(this.SELECTED_VIDEO_DETAILS);
  }

  clearRazorpayData(): void {
    localStorage.removeItem(this.RZP_CHECKOUT_ANON_ID);
    localStorage.removeItem(this.RZP_DEVICE_ID);
    localStorage.removeItem(this.RZP_STORED_CHECKOUT_ID);
    localStorage.removeItem(this.DISCOUNTS);
    localStorage.removeItem(this.REGISTER_TYPE_SELECTED);
  }

  /**
   * Clears the summary ID from storage.
   */
  clearSummaryId(): void {
    localStorage.removeItem(this.SUMMARY_ID);
  }

  /**
   * Removes the "state" item from the browser's localStorage.
   * Use this method to clear any stored state information.
   */
  clearState(): void {
    localStorage.removeItem(this.STATE);
  }

  /**
   * Removes the 'JUST_AUTHENTICATED' flag from local storage.
   * This method is typically called to clear the authentication state
   * after it has been processed, ensuring that the flag does not persist
   * across sessions or page reloads.
   */
  clearJustAuthenticated(): void {
    localStorage.removeItem(this.JUST_AUTHENTICATED);
  }

  /**
   * Removes the virtual event pending flag from local storage.
   *
   * This method deletes the item associated with the `VIRTUAL_EVENT_PENDING` key
   * from the browser's local storage, effectively clearing any pending virtual event state.
   */
  clearVirtualEventPending(): void {
    localStorage.removeItem(this.VIRTUAL_EVENT_PENDING);
  }

  clearRefSource(): void {
    localStorage.removeItem(this.REF_SOURCE);
  }

  clearPavilianData(): void {
    localStorage.removeItem(this.SELECTED_PAVILION_NAME);
    localStorage.removeItem(this.SELECTED_PAVILION_IMG_URL);
    localStorage.removeItem(this.SELECTED_PAVILION_CODE);
  }

}
